import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const Dashboard = ({ currentUserId, appMode }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidCounts, setBidCounts] = useState({});
  const [showBidReview, setShowBidReview] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingData, setRatingData] = useState({ rating: 5, review: '' });
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [editingListing, setEditingListing] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [listingToDelete, setListingToDelete] = useState(null);

  useEffect(() => {
    if (!currentUserId || (appMode !== 'Agent' && appMode !== 'Private Owner')) {
      navigate('/marketplace');
      return;
    }
    fetchDashboardData();
  }, [currentUserId, appMode]);

  const fetchDashboardData = async () => {
    try {
      const [listingsRes, campaignsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/listings/owner/${currentUserId}`),
        axios.get(`${API}/campaigns/owner/${currentUserId}`),
        axios.get(`${API}/messages/owner/${currentUserId}`)
      ]);
      setListings(listingsRes.data);
      setCampaigns(campaignsRes.data);
      setMessages(messagesRes.data);

      // Fetch aggregated bid counts for all owner campaigns in one request
      const ids = (campaignsRes.data || []).map(c => c.id).filter(Boolean);
      if (ids.length > 0) {
        try {
          const countsRes = await axios.get(
            `${API}/bids/counts?campaignIds=${encodeURIComponent(ids.join(','))}`
          );
          setBidCounts(countsRes.data?.counts || {});
        } catch (err) {
          console.error('Failed to fetch bid counts:', err);
          setBidCounts({});
        }
      } else {
        setBidCounts({});
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
  };

  const handleReviewBids = async (campaign) => {
    setSelectedCampaign(campaign);
    try {
      const response = await axios.get(`${API}/bids/campaign/${campaign.id}`);
      setBids(response.data);
      setShowBidReview(true);
    } catch (error) {
      console.error('Error fetching bids:', error);
      toast.error('Failed to load bids');
    }
  };

  const handleAcceptBid = async (bid) => {
    try {
      await axios.put(`${API}/bids/${bid.id}/accept`);
      await axios.put(`${API}/campaigns/${selectedCampaign.id}/close?winnerBidId=${bid.id}`);
      toast.success('Bid accepted and campaign closed!');
      setShowBidReview(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error accepting bid:', error);
      toast.error('Failed to accept bid');
    }
  };

  const handleSubmitRating = async () => {
    if (!ratingData.review || ratingData.review.trim().split(' ').length < 10) {
      toast.error('Review must be at least 10 words');
      return;
    }

    try {
      await axios.post(`${API}/ratings`, {
        campaignId: selectedCampaign.id,
        influencerId: selectedInfluencer,
        agentId: currentUserId,
        rating: ratingData.rating,
        review: ratingData.review
      });
      toast.success('Rating submitted successfully!');
      setShowRatingModal(false);
      setRatingData({ rating: 5, review: '' });
      fetchDashboardData();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating');
    }
  };

  const handleDeleteListing = async (listingId) => {
    try {
      await axios.delete(`${API}/listings/${listingId}`);
      toast.success('Listing deleted successfully!');
      setShowDeleteDialog(false);
      setListingToDelete(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast.error('Failed to delete listing');
    }
  };

  const handleEditListing = (listing) => {
    navigate('/new-listing', { state: { editListing: listing } });
  };

  const formatZAR = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (showBidReview) {
    return (
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
        <Button onClick={() => setShowBidReview(false)} variant="ghost" className="mb-4 text-sm" data-testid="back-to-dashboard">
          <i className="fas fa-arrow-left mr-2"></i> Back to Portal
        </Button>
        
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl md:text-2xl text-red-700">Review Bids for: {selectedCampaign.title}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            {bids.length === 0 ? (
              <p className="text-center text-gray-500 py-8 text-sm sm:text-base">No bids submitted yet.</p>
            ) : (
              <div className="space-y-4">
                {bids.map(bid => (
                  <div key={bid.id} className="border rounded-lg p-3 sm:p-4 hover:bg-gray-50" data-testid="bid-item">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-3">
                      {/* Profile Photo */}
                      {bid.profilePhotoUrl && (
                        <div className="flex-shrink-0">
                          <img 
                            src={bid.profilePhotoUrl} 
                            alt="Influencer profile" 
                            className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-purple-300 shadow-sm"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm sm:text-base">Influencer ID: {bid.influencerId}</p>
                        <p className="text-xs sm:text-sm text-gray-600">Platform: {bid.platform} | Followers: {bid.followers.toLocaleString()}</p>
                        
                        {/* Contact Information */}
                        {(bid.whatsappNumber || bid.socialMediaHandles) && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-xs font-semibold text-blue-700 mb-2">Contact Information:</p>
                            {bid.whatsappNumber && (
                              <p className="text-sm">
                                <i className="fab fa-whatsapp text-green-600 mr-2"></i>
                                <a href={`https://wa.me/${bid.whatsappNumber.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                  {bid.whatsappNumber}
                                </a>
                              </p>
                            )}
                            {bid.socialMediaHandles && (
                              <div className="mt-2 flex flex-wrap gap-3 text-sm">
                                {bid.socialMediaHandles.instagram && (
                                  <span>
                                    <i className="fab fa-instagram text-pink-600 mr-1"></i>
                                    <a href={`https://instagram.com/${bid.socialMediaHandles.instagram}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                      @{bid.socialMediaHandles.instagram}
                                    </a>
                                  </span>
                                )}
                                {bid.socialMediaHandles.tiktok && (
                                  <span>
                                    <i className="fab fa-tiktok mr-1"></i>
                                    <a href={`https://tiktok.com/@${bid.socialMediaHandles.tiktok}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                      @{bid.socialMediaHandles.tiktok}
                                    </a>
                                  </span>
                                )}
                                {bid.socialMediaHandles.facebook && (
                                  <span>
                                    <i className="fab fa-facebook text-blue-600 mr-1"></i>
                                    <a href={`https://facebook.com/${bid.socialMediaHandles.facebook}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                      {bid.socialMediaHandles.facebook}
                                    </a>
                                  </span>
                                )}
                                {bid.socialMediaHandles.twitter && (
                                  <span>
                                    <i className="fab fa-twitter text-blue-400 mr-1"></i>
                                    <a href={`https://twitter.com/${bid.socialMediaHandles.twitter}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                      @{bid.socialMediaHandles.twitter}
                                    </a>
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap ${
                        bid.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {bid.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-emerald-600 mb-2">Proposed Amount: {formatZAR(bid.proposedAmount)}</p>
                    <p className="text-sm text-gray-700 mb-4">{bid.message}</p>
                    {bid.status === 'pending' && (
                      <Button onClick={() => handleAcceptBid(bid)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="accept-bid-btn">
                        Accept Bid
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8 overflow-x-hidden">
      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
        {appMode === 'Agent' ? 'Your Agent Portal' : 'Your Private Owner Portal'}
      </h2>

      {/* Agent Profile Banner */}
      {appMode === 'Agent' && (
        <div className="mb-4 sm:mb-6 bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
              <div className="bg-blue-600 text-white p-2 sm:p-3 rounded-full flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-gray-800">Complete Your Agent Profile</h3>
                <p className="text-xs sm:text-sm text-gray-600">Submit your information and documents for verification</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/agent-profile')}
              className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto text-sm sm:text-base"
            >
              Go to Profile →
            </Button>
          </div>
        </div>
      )}

      {/* Private Owner Verification Banner */}
      {appMode === 'Private Owner' && (
        <div className="mb-4 sm:mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start sm:items-center gap-3 sm:gap-4">
              <div className="bg-purple-600 text-white p-2 sm:p-3 rounded-full flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-gray-800">Verify Your Identity</h3>
                <p className="text-xs sm:text-sm text-gray-600">Submit your ID and proof of ownership documents</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/owner-profile')}
              className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto text-sm sm:text-base"
            >
              Verify Profile →
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-4 sm:space-y-6">
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl md:text-2xl text-emerald-700 flex items-center">
              <i className="fas fa-home mr-2"></i> Your Listings ({listings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            {listings.length === 0 ? (
              <p className="text-gray-500 italic text-sm sm:text-base">You have no active listings. Go to "New Listing" to submit one.</p>
            ) : (
              <div className="space-y-3">
                {listings.map(listing => (
                  <div key={listing.id} className="p-3 sm:p-4 border rounded-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-gray-50 hover:bg-gray-100 transition-colors" data-testid="listing-item">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">{listing.title}</p>
                      <p className="text-xs sm:text-sm text-gray-600">{formatZAR(listing.price)} ({listing.type.toUpperCase()})</p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEditListing(listing)}
                        data-testid="edit-listing-btn"
                        className="text-xs sm:text-sm flex-1 sm:flex-none"
                      >
                        <i className="fas fa-edit mr-1"></i> Edit
                      </Button>
                      <Dialog open={showDeleteDialog && listingToDelete?.id === listing.id} onOpenChange={setShowDeleteDialog}>
                        <DialogTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs sm:text-sm flex-1 sm:flex-none"
                            onClick={() => setListingToDelete(listing)}
                            data-testid="delete-listing-btn"
                          >
                            <i className="fas fa-trash mr-1"></i> Delete
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="mx-4 max-w-md">
                          <DialogHeader>
                            <DialogTitle className="text-lg">Delete Listing</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <p className="text-sm sm:text-base">Are you sure you want to delete <span className="font-semibold">{listing.title}</span>?</p>
                            <p className="text-xs sm:text-sm text-gray-600">This action cannot be undone.</p>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} size="sm">Cancel</Button>
                              <Button 
                                className="bg-red-600 hover:bg-red-700"
                                onClick={() => handleDeleteListing(listing.id)}
                                size="sm"
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-lg sm:text-xl md:text-2xl text-red-700 flex items-center">
              <i className="fas fa-bullhorn mr-2"></i> Your Campaigns ({campaigns.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
            {/* Payment Responsibility Notice */}
            <Card className="mb-4 bg-yellow-50 border-yellow-300">
              <CardContent className="p-3 sm:pt-4 sm:p-4">
                <div className="flex items-start gap-2 sm:gap-3">
                  <i className="fas fa-exclamation-triangle text-yellow-600 text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                  <div className="min-w-0">
                    <p className="font-semibold text-yellow-900 mb-1 text-sm sm:text-base">Payment Notice</p>
                    <p className="text-xs sm:text-sm text-yellow-800">
                      Flippy Homes does not process payments. Pay influencers directly upon accepting their bid.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {campaigns.length === 0 ? (
              <p className="text-gray-500 italic text-sm sm:text-base">You have no active campaigns. Go to "Launch Campaign" to start one.</p>
            ) : (
              <div className="space-y-3">
                {campaigns.map(campaign => {
                  const isOpen = campaign.status === 'open';
                  const isClosed = campaign.status === 'closed';
                  
                  return (
                    <div key={campaign.id} className="p-3 sm:p-4 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors" data-testid="campaign-item">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 text-sm sm:text-base flex flex-wrap items-center gap-2">
                            <span className="truncate">{campaign.title}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
                              isOpen ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                            }`}>
                              {campaign.status.toUpperCase()}
                            </span>
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">
                            <span className="inline-block mr-2">Platform: <span className="font-bold text-red-500">{campaign.platform}</span></span>
                            <span className="inline-block mr-2">Budget: {formatZAR(campaign.budget)}</span>
                            <span className="inline-block">Followers: {campaign.minFollowers.toLocaleString()}+</span>
                          </p>
                          {/* Bid count — aggregated from /api/bids/counts */}
                          <p
                            className="text-xs text-gray-500 flex items-center gap-1 mt-1"
                            data-testid="bid-count"
                          >
                            <i className="fas fa-users"></i>
                            {bidCounts[campaign.id] > 0
                              ? `${bidCounts[campaign.id]} ${bidCounts[campaign.id] === 1 ? 'bid submitted' : 'bids submitted'}`
                              : 'No bids yet'}
                          </p>
                        </div>
                        
                        <div className="flex gap-2 flex-shrink-0">
                          {isOpen && (
                            <Button
                              onClick={() => handleReviewBids(campaign)}
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-xs sm:text-sm"
                              data-testid="review-bids-btn"
                            >
                              Review Bids
                            </Button>
                          )}
                          {isClosed && campaign.winnerBidId && (
                            <>
                              <Button
                                onClick={() => handleReviewBids(campaign)}
                                size="sm"
                                variant="outline"
                                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-xs sm:text-sm"
                                data-testid="view-winner-btn"
                              >
                                <i className="fas fa-user-check mr-1"></i> <span className="hidden sm:inline">View </span>Winner
                              </Button>
                              <Dialog open={showRatingModal} onOpenChange={setShowRatingModal}>
                                <DialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    className="bg-yellow-600 hover:bg-yellow-700"
                                    onClick={() => {
                                      setSelectedCampaign(campaign);
                                      // Find winner influencer ID from bids
                                      axios.get(`${API}/bids/campaign/${campaign.id}`).then(res => {
                                        const winnerBid = res.data.find(b => b.id === campaign.winnerBidId);
                                        if (winnerBid) setSelectedInfluencer(winnerBid.influencerId);
                                      });
                                    }}
                                    data-testid="rate-influencer-btn"
                                  >
                                    Rate Influencer
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle className="text-yellow-600">Rate Your Influencer</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <p className="text-sm">Campaign: <span className="font-semibold">{campaign?.title}</span></p>
                                    
                                    <div>
                                      <label className="block text-sm font-medium mb-2">Rating (1-5 Stars)</label>
                                      <div className="flex gap-2">
                                        {[1, 2, 3, 4, 5].map(star => (
                                          <button
                                            key={star}
                                            type="button"
                                            onClick={() => setRatingData({ ...ratingData, rating: star })}
                                            className="text-3xl transition-colors"
                                          >
                                            <i className={`${
                                              star <= ratingData.rating ? 'fas fa-star text-yellow-500' : 'far fa-star text-gray-300'
                                            }`}></i>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    
                                    <div>
                                      <label className="block text-sm font-medium mb-2">Review (min. 10 words)</label>
                                      <Textarea
                                        value={ratingData.review}
                                        onChange={(e) => setRatingData({ ...ratingData, review: e.target.value })}
                                        placeholder="Describe your experience with the influencer's performance."
                                        rows={4}
                                      />
                                    </div>
                                    
                                    <Button onClick={handleSubmitRating} className="w-full bg-yellow-600 hover:bg-yellow-700">
                                      Submit Review
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-blue-700 flex items-center">
              <i className="fas fa-envelope mr-2"></i> Guest Messages ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <p className="text-gray-500 italic">No messages from guests yet.</p>
            ) : (
              <div className="space-y-3">
                {messages.map(message => (
                  <div key={message.id} className="p-4 border rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors" data-testid="message-item">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{message.guestName}</p>
                        <p className="text-xs text-gray-600">Property: {message.listingTitle}</p>
                      </div>
                      <span className="text-xs text-gray-500">{new Date(message.createdAt).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{message.message}</p>
                    <div className="flex gap-4 text-sm">
                      <a href={`mailto:${message.guestEmail}`} className="text-blue-600 hover:underline">
                        <i className="fas fa-envelope mr-1"></i> {message.guestEmail}
                      </a>
                      {message.guestPhone && (
                        <a href={`tel:${message.guestPhone}`} className="text-blue-600 hover:underline">
                          <i className="fas fa-phone mr-1"></i> {message.guestPhone}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default Dashboard;